import React, { useState, useCallback, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import IpdAdvanceReceipt from './IpdAdvanceReceipt';

const IPDAdvanceTable = () => {
  const [selectedIpd, setSelectedIpd] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [advanceReceiptData, setAdvanceReceiptData] = useState(null);
  const [showAdvanceReceipt, setShowAdvanceReceipt] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch company details
  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
   if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to load company details");
          return res.json();
        })
        .then((data) => setCompanyDetails(data))
        .catch(console.error);
    }
  }, []);

  // Auto-dismiss alerts
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleAdvancePreview = async (record) => {
  try {
      const res = await customFetch(`/IpdAdvDt/receipt/${record.advID}`);

      if (!res.ok) throw new Error("Failed to load receipt data");

      const data = await res.json();
      setAdvanceReceiptData(data);
      setShowAdvanceReceipt(true);
    } catch (err) {
      console.error(err);
      setError("Failed to load receipt");
    }
  };

  const handleAdvanceReceiptClose = () => {
    setShowAdvanceReceipt(false);
    setAdvanceReceiptData(null);
  };

  const loadIpdOptions = async (inputValue) => {
    if (!inputValue) return [];
   try {
      const res = await customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`);
      
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      
      return data.map((item) => {
        // ✅ Changed ipD_Date to regDate 
        const regDateFormatted = item.regDate 
            ? new Date(item.regDate).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";

        return {
          // ✅ Label updated to show Reg Date
          label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
          value: item.ipD_ID,
          admDate: item.regDate, // Isko regDate se map kar diya taaki form mein save karte waqt yahi date jaye
          name: item.name,
        };
      });
    } catch { 
      return []; 
    }
  };
  // Fetch advance records
  const fetchRecords = useCallback(async (ipdId) => {
    if (!ipdId) {
      setRecords([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await customFetch(`/IpdAdvDt/search-by-ipd?ipdId=${ipdId}`);
      if (!res.ok) throw new Error("Failed to fetch records");
      const data = await res.json();
      setRecords(data);
    } catch (e) {
      setError(e.message || "Failed to load records");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleIpdChange = (option) => {
    setSelectedIpd(option);
    fetchRecords(option ? option.value : null);
  };

  const startEditing = (record) => {
    setEditId(record.advID);
    setEditData({
      adv_Amt: record.adv_Amt || "",
      remarks: record.remarks || "",
      payMode: record.payMode || "Cash",
      chqNo: record.chqNo || "",
      chqDate: record.chqDate ? record.chqDate.substring(0, 10) : "",
      recvDate: record.recvDate ? record.recvDate.substring(0, 10) : "",
      entrydate: (record.entrydate || record.ENTRYDATE) ? (record.entrydate || record.ENTRYDATE).substring(0, 16) : "", 
    });
    setError(null);
    setMessage(null);
  };

  const handleChange = (field, value) => {
    setEditData(prev => {
      const updated = { ...prev, [field] : value };
      
      if (field === "payMode" && (value === "Cash" || value === "UPI" || value === "Card")) {
        updated.chqNo = "";
        updated.chqDate = "";
      }
      
      return updated;
    });
  };

  const saveRecord = async () => {
    if (!editId) return;
    
    const amtNum = parseFloat(editData.adv_Amt);
    if (isNaN(amtNum) || amtNum <= 0) {
      setError("Advance amount must be a positive number.");
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    
    try {
   
      const originalRecord = records.find(r => r.advID === editId);
      if (!originalRecord) {
        throw new Error("Record not found");
      }

      const updatedRecord = {
        ...originalRecord,
        adv_Amt: amtNum,
        recvDate: editData.recvDate || originalRecord.recvDate,
        remarks: editData.remarks,
        payMode: editData.payMode,
        chqNo: editData.chqNo || null,
        chqDate: editData.chqDate || null,
        ENTRYDATE: editData.entrydate || null 
      };

    const res = await customFetch(`/IpdAdvDt/${editId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updatedRecord),
      });

      if (res.status === 403) {
        setError("You do not have permission to edit.");
      } else if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to update record.");
      } else {
        setMessage("Record updated successfully.");

        setRecords(prev => {
          const updated = [...prev];
          const index = updated.findIndex(r => r.advID === editId);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              adv_Amt: amtNum,
              recvDate: editData.recvDate,
              remarks: editData.remarks,
              payMode: editData.payMode,
              chqNo: editData.chqNo,
              chqDate: editData.chqDate,
              entrydate: editData.entrydate, 
              ENTRYDATE: editData.entrydate 
            };
          }
          return updated;
        });

        setEditId(null);
        setEditData({});
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData({});
    setError(null);
    setMessage(null);
  };

  const deleteRecord = async (id) => {
    if (!window.confirm("Are you sure you want to delete this advance payment?")) return;

    setLoading(true);
    setError(null);
    setMessage(null);
    
  try {
      const res = await customFetch(`/IpdAdvDt/${id}`, {
        method: "DELETE"
      });
      
      if (!res.ok) {
        const text = await res.text();
        setError(text || "Failed to delete record.");
      } else {
        setMessage("Record deleted successfully.");
        setRecords(prev => prev.filter(r => r.advID !== id));
        if (editId === id) cancelEdit();
      }
    } catch (e) {
      setError("Network error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(record => 
    (record.remarks?.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (record.payMode?.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (record.chqNo?.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (record.adv_Amt?.toString().includes(searchQuery)) ||
    (record.receiptNo?.toString().includes(searchQuery))
  );

  return (
    <div className="ipd-advance-table-container compact-ui">
      <div className="ipd-advance-table-card">
        {/* Header - Solid Color */}
        <div className="table-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-table"></i>
            </div>
            <div className="header-text">
              <h5>IPD Advance Records</h5>
              <p>View and manage advance payment history</p>
            </div>
          </div>
          <div className="header-badge">
            <span className="badge-count">{filteredRecords.length}</span>
            <span className="badge-label">Records</span>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError(null)}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
        {message && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{message}</span>
            <button className="alert-close" onClick={() => setMessage(null)}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        <div className="table-body">
          {/* Search Section */}
          <div className="search-section">
            <div className="patient-search">
              <label>
                <i className="bi bi-person-search"></i>
                Search Patient
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadIpdOptions}
                onChange={handleIpdChange}
                value={selectedIpd}
                isClearable
                placeholder="Type patient name, mobile or Sr.No..."
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '30px', /* ✅ Compact Height */
                    height: '30px',
                    fontSize: '11px', /* ✅ Compact Font */
                    borderRadius: '6px',
                    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
                    boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 0, 0.05)' : 'none',
                    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
                  }),
                  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
                  input: (base) => ({ ...base, margin: '0', padding: '0' }),
                  indicatorsContainer: (base) => ({ ...base, height: '30px' }),
                  dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
                  clearIndicator: (base) => ({ ...base, padding: '4px' }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: '11px',
                    padding: '6px 10px',
                    backgroundColor: state.isFocused ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' : 'white',
                    color: '#333',
                    cursor: 'pointer'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    fontSize: '11px'
                  })
                }}
              />
            </div>

            {selectedIpd && (
              <div className="record-search">
                <div className="search-box">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    placeholder="Search in records (amount, payment mode, remarks)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Table Section */}
          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading records...</p>
              </div>
            ) : !selectedIpd ? (
              <div className="empty-state">
                <i className="bi bi-search"></i>
                <h6>No Patient Selected</h6>
                <p>Please search and select a patient to view advance records</p>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox"></i>
                <h6>No Records Found</h6>
                <p>{searchQuery ? "No records match your search" : "No advance payments found for this patient"}</p>
              </div>
            ) : (
              <table className="records-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }} className="text-center">
                      <i className="bi bi-eye"></i>
                    </th>
                    <th className="text-center">Receipt No.</th>
                    <th>Amount</th>
                    <th>Entry Date</th>
                    <th>Received Date</th>
                    <th>Payment Mode</th>
                    <th>Cheque No.</th>
                    <th>Cheque Date</th>
                    <th>Remarks</th>
                    <th style={{ width: '80px' }} className="text-center">
                      <i className="bi bi-tools"></i> Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map(record => (
                    <tr key={record.advID}>
                      {/* Preview */}
                      <td className="text-center">
                        <button
                          className="btn-icon btn-preview"
                          onClick={() => handleAdvancePreview(record)}
                          title="Preview Receipt"
                        >
                          <i className="bi bi-eye-fill"></i>
                        </button>
                      </td>
            <td className="text-center">
  <span style={{ fontWeight: '600', color: 'var(--theme-color, #0088cc)' }}>
    {record.receiptNo ? record.receiptNo : `ADV-${record.advID}`}
  </span>
</td>

                      {/* Amount */}
                      <td>
                        {editId === record.advID ? (
                          <input
                            type="number"
                            className="form-control-inline"
                            value={editData.adv_Amt}
                            onChange={e => handleChange('adv_Amt', e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        ) : (
                          <span className="amount-badge">
                            <i className="bi bi-currency-rupee"></i>
                            {record.adv_Amt?.toFixed(2)}
                          </span>
                        )}
                      </td>

                      {/* Entry Date */}
                      <td>
                        {editId === record.advID ? (
                          <input
                            type="datetime-local"
                            className="form-control-inline"
                            value={editData.entrydate || ''}
                            onChange={e => handleChange('entrydate', e.target.value)}
                          />
                        ) : (
                          <span className="date-text">
                            <i className="bi bi-calendar-event"></i>
                            {(record.entrydate || record.ENTRYDATE) ? 
                              new Date(record.entrydate || record.ENTRYDATE).toLocaleString('en-IN', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', hour12: true
                              }) : '-'
                            }
                          </span>
                        )}
                      </td>

                      {/* Received Date */}
                      <td>
                        {editId === record.advID ? (
                          <input
                            type="date"
                            className="form-control-inline"
                            value={editData.recvDate || ''}
                            onChange={e => handleChange('recvDate', e.target.value)}
                            disabled
                          />
                        ) : (
                          <span className="date-text">
                            <i className="bi bi-calendar-check"></i>
                            {record.recvDate ? new Date(record.recvDate).toLocaleDateString('en-IN') : '-'}
                          </span>
                        )}
                      </td>

                      {/* Payment Mode */}
                      <td>
                        {editId === record.advID ? (
                          <select
                            className="form-control-inline"
                            value={editData.payMode}
                            onChange={e => handleChange('payMode', e.target.value)}
                          >
                            <option value="Cash">Cash</option>
                            <option value="Card">Card</option>
                            <option value="UPI">UPI</option>
                            <option value="Cheque">Cheque</option>
                          </select>
                        ) : (
                          <span className={`payment-badge payment-${record.payMode?.toLowerCase()}`}>
                            {record.payMode === 'Cash' && <i className="bi bi-cash"></i>}
                            {record.payMode === 'Card' && <i className="bi bi-credit-card"></i>}
                            {record.payMode === 'UPI' && <i className="bi bi-phone"></i>}
                            {record.payMode === 'Cheque' && <i className="bi bi-receipt"></i>}
                            {record.payMode}
                          </span>
                        )}
                      </td>

                      {/* Cheque Number */}
                      <td>
                        {editId === record.advID ? (
                          <input
                            type="text"
                            className="form-control-inline"
                            value={editData.chqNo || ''}
                            onChange={e => handleChange('chqNo', e.target.value)}
                            disabled={editData.payMode === "Cash" || editData.payMode === "UPI" || editData.payMode === "Card"}
                          />
                        ) : (
                          record.chqNo || <span className="text-muted">-</span>
                        )}
                      </td>

                      {/* Cheque Date */}
                      <td>
                        {editId === record.advID ? (
                          <input
                            type="date"
                            className="form-control-inline"
                            value={editData.chqDate || ''}
                            onChange={e => handleChange('chqDate', e.target.value)}
                            disabled={editData.payMode === "Cash" || editData.payMode === "UPI" || editData.payMode === "Card"}
                          />
                        ) : (
                          record.chqDate ? new Date(record.chqDate).toLocaleDateString('en-IN') : <span className="text-muted">-</span>
                        )}
                      </td>

                      {/* Remarks */}
                      <td>
                        {editId === record.advID ? (
                          <input
                            type="text"
                            className="form-control-inline"
                            value={editData.remarks || ''}
                            onChange={e => handleChange('remarks', e.target.value)}
                            placeholder="Add remarks..."
                          />
                        ) : (
                          <span className="remarks-text">{record.remarks || <span className="text-muted">-</span>}</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="text-center">
                        {editId === record.advID ? (
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-save"
                              onClick={saveRecord}
                              disabled={loading}
                              title="Save"
                            >
                              <i className="bi bi-check-circle"></i>
                            </button>
                            <button
                              className="btn-icon btn-cancel"
                              onClick={cancelEdit}
                              disabled={loading}
                              title="Cancel"
                            >
                              <i className="bi bi-x-circle"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              className="btn-icon btn-edit"
                              onClick={() => startEditing(record)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => deleteRecord(record.advID)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {companyDetails && showAdvanceReceipt && advanceReceiptData && (
        <IpdAdvanceReceipt
          show={showAdvanceReceipt}
          data={advanceReceiptData}
          companyDetails={companyDetails}
          onClose={handleAdvanceReceiptClose}
          onPrint={() => window.print()}
        />
      )}

      {/* Styles - COMPACT UI APPLIED */}
      <style jsx>{`
        .compact-ui {
          font-family: system-ui, -apple-system, sans-serif;
        }

        .ipd-advance-table-container {
          padding: 0;
        }

        .ipd-advance-table-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          border: 1px solid #e0e6ed;
          overflow: hidden;
        }

        /* Animations */
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .slide-in {
          animation: slideIn 0.2s ease;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Header */
        .table-header {
          background: var(--theme-color, #0088cc);
          padding: 10px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #00d4aa;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .header-icon {
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: white;
        }

        .header-text h5 {
          color: white;
          margin: 0;
          font-weight: 600;
          font-size: 15px;
          line-height: 1.1;
        }

        .header-text p {
          color: rgba(255, 255, 255, 0.85);
          margin: 0;
          font-size: 10px;
        }

        .header-badge {
          background: rgba(255, 255, 255, 0.95);
          padding: 4px 10px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .badge-count {
          font-size: 14px;
          font-weight: 700;
          color: var(--theme-color, #0088cc);
        }

        .badge-label {
          font-size: 10px;
          color: #666;
          font-weight: 500;
        }

        /* Alerts */
        .alert-custom {
          margin: 10px 16px 0;
          padding: 8px 12px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 500;
          position: relative;
        }

        .alert-danger { background: #fee; color: #c33; border: 1px solid #fcc; }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }

        .alert-custom i { font-size: 12px; }
        .alert-close {
          position: absolute;
          right: 8px;
          background: transparent;
          border: none;
          cursor: pointer;
          opacity: 0.6;
          padding: 0;
        }
        .alert-close:hover { opacity: 1; }

        /* Body */
        .table-body {
          padding: 12px 16px;
        }

        /* Search Section */
        .search-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 12px;
          margin-bottom: 12px;
        }

        .patient-search {
          flex: 1;
        }

        .patient-search label {
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 4px;
          font-size: 11px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .patient-search label i {
          color: var(--theme-color, #0088cc);
        }

        .record-search {
          flex: 1;
          display: flex;
          justify-content: flex-end;
        }

        .search-box {
          position: relative;
          width: 100%;
          max-width: 300px;
        }

        .search-box i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          font-size: 12px;
        }

        .search-box input {
          width: 100%;
          padding: 6px 10px 6px 28px;
          border: 1.5px solid #cbd5e0;
          border-radius: 6px;
          font-size: 11px;
          transition: all 0.2s;
          height: 30px;
        }

        .search-box input:focus {
          outline: none;
          border-color: var(--theme-color, #0088cc);
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
        }

        /* Table */
        .table-container {
          background: #ffffff;
          border-radius: 8px;
          border: 1px solid #e0e6ed;
          overflow-x: auto;
        }

        .records-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 11px;
        }

        .records-table thead tr {
          background: var(--theme-color, #0088cc);
        }

        .records-table thead th {
          padding: 8px;
          text-align: left;
          font-weight: 600;
          color: white;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
          border: none;
        }

        .records-table thead th:first-child { border-top-left-radius: 6px; }
        .records-table thead th:last-child { border-top-right-radius: 6px; }

        .records-table tbody tr {
          background: white;
          transition: all 0.2s;
        }

        .records-table tbody tr:hover {
          background: #f8f9fa;
          box-shadow: inset 0 0 0 9999px rgba(0,0,0,0.02);
        }

        .records-table tbody td {
          padding: 6px 8px;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
        }

        /* Form Controls Inline */
        .form-control-inline {
          width: 100%;
          padding: 4px 6px;
          border: 1px solid #cbd5e0;
          border-radius: 4px;
          font-size: 11px;
          height: 26px;
          transition: all 0.2s;
        }

        .form-control-inline:focus {
          outline: none;
          border-color: var(--theme-color, #0088cc);
        }

        .form-control-inline:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
          opacity: 0.7;
        }

        /* Badges */
        .amount-badge {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          background: #dcfce7;
          color: #166534;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 11px;
        }

        .payment-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 500;
          font-size: 10px;
        }

        .payment-cash { background: #d1fae5; color: #065f46; }
        .payment-card { background: #dbeafe; color: #1e40af; }
        .payment-upi { background: #fce7f3; color: #9f1239; }
        .payment-cheque { background: #fef3c7; color: #92400e; }

        .date-text {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: #4b5563;
          font-size: 11px;
          white-space: nowrap;
        }

        .date-text i { color: var(--theme-color, #0088cc); }

        .remarks-text { color: #4b5563; font-size: 11px; font-style: italic; }
        .text-muted { color: #9ca3af; font-size: 11px; }

        /* Action Buttons */
        .text-center { text-align: center; }
        
        .action-buttons {
          display: flex;
          gap: 4px;
          justify-content: center;
        }

        .btn-icon {
          width: 24px;
          height: 24px;
          border: none;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 11px;
        }

        .btn-preview { background: #e0f2fe; color: #0369a1; }
        .btn-preview:hover { background: #0369a1; color: white; transform: translateY(-1px); }

        .btn-edit { background: #dbeafe; color: #1e40af; }
        .btn-edit:hover { background: #1e40af; color: white; transform: translateY(-1px); }

        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; transform: translateY(-1px); }

        .btn-save { background: #d1fae5; color: #065f46; }
        .btn-save:hover { background: #059669; color: white; transform: translateY(-1px); }

        .btn-cancel { background: #f3f4f6; color: #4b5563; }
        .btn-cancel:hover { background: #6b7280; color: white; transform: translateY(-1px); }

        .btn-icon:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

        /* Loading & Empty State */
        .loading-state, .empty-state {
          text-align: center;
          padding: 40px;
        }

        .spinner {
          width: 30px;
          height: 30px;
          margin: 0 auto 10px;
          border: 3px solid #e9ecef;
          border-top-color: var(--theme-color, #0088cc);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-state p { color: #6b7280; font-size: 11px; }

        .empty-state i { font-size: 36px; color: #d1d5db; margin-bottom: 10px; display: block; }
        .empty-state h6 { color: #374151; font-weight: 600; margin-bottom: 4px; font-size: 13px; }
        .empty-state p { color: #6b7280; font-size: 11px; margin: 0; }

        /* Responsive */
        @media (max-width: 768px) {
          .search-section {
            flex-direction: column;
            align-items: stretch;
          }
          .record-search {
            justify-content: flex-start;
          }
          .search-box { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default IPDAdvanceTable;